import React, { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, QrCode, ExternalLink, AlertTriangle, XCircle, Smartphone } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { config } from '../../config'
import { useAuth } from '../../AuthProvider'

// Import preview components
import { WebsiteProductionWrapper } from '../qr/WebsiteProductionWrapper'
import { SpotifyProductionWrapper } from '../qr/SpotifyProductionWrapper'
import { YouTubeProductionWrapper } from '../qr/YouTubeProductionWrapper'
import { PDFProductionWrapper } from '../qr/PDFProductionWrapper'
import { MultiShopProductionWrapper } from '../qr/MultiShopProductionWrapper'
import { AuthorProductionWrapper } from '../qr/AuthorProductionWrapper'
import { BookSampleProductionWrapper } from '../qr/BookSampleProductionWrapper'

interface QRLink {
  id: string
  title: string
  slug: string
  type: string
  target_url?: string
  pdf_url?: string
  metadata?: any
  health_status?: string
  health_error?: string
  scans?: number
  created_at: string
  author_account_slug?: string
}

export const QRPreviewCarousel: React.FC = () => {
  const [qrLinks, setQrLinks] = useState<QRLink[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const { session } = useAuth()
  const navigate = useNavigate()
  
  // Number of QR codes to show at once
  const qrsPerView = window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3

  useEffect(() => {
    loadQRLinks()
  }, [session])

  const loadQRLinks = async () => {
    if (!session?.access_token) return
    
    try {
      const response = await fetch(`${config.API_BASE}/api/links`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        // Sort by created_at descending to show newest first
        const sortedLinks = data.sort((a: QRLink, b: QRLink) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        // Limit to first 10 for performance
        setQrLinks(sortedLinks.slice(0, 10))
      }
    } catch (error) {
      console.error('Error loading QR links:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePrevious = () => {
    setCurrentIndex(prev => Math.max(0, prev - qrsPerView))
  }

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(qrLinks.length - qrsPerView, prev + qrsPerView))
  }

  const handleQRClick = (qrLink: QRLink) => {
    navigate(`/qr/dash/links?edit=${qrLink.id}&focus=true`)
  }

  const getPreviewComponent = (qrLink: QRLink) => {
    switch (qrLink.type) {
      case 'spotify':
        return (
          <SpotifyProductionWrapper 
            linkData={qrLink}
            isPreview={true}
          />
        )
      case 'youtube':
        return (
          <YouTubeProductionWrapper 
            linkData={qrLink}
            isPreview={true}
          />
        )
      case 'pdf':
        return (
          <PDFProductionWrapper 
            linkData={{
              ...qrLink,
              pdf_url: qrLink.pdf_url || qrLink.target_url || ''
            }}
            isPreview={true}
          />
        )
      case 'multishop':
        return (
          <MultiShopProductionWrapper 
            linkData={qrLink}
            isPreview={true}
          />
        )
      case 'author':
        return <AuthorProductionWrapper linkData={qrLink} isPreview={true} />
      case 'sample':
        return (
          <BookSampleProductionWrapper 
            linkData={qrLink}
            isPreview={true}
          />
        )
      case 'website':
      default:
        return (
          <WebsiteProductionWrapper 
            linkData={qrLink}
            isPreview={true}
          />
        )
    }
  }

  const getQRCodeUrl = (qrLink: QRLink) => {
    const base = `${config.API_BASE}/api/links/qr/${qrLink.slug}?size=150`
    const styled = qrLink.metadata && qrLink.metadata.styleConfig ? '&styled=1' : ''
    const tsSource = (qrLink as any).updated_at || (qrLink as any).created_at || Date.now()
    const ts = encodeURIComponent(String(tsSource))
    return `${base}${styled}&t=${ts}`
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="flex gap-4">
            <div className="w-full h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (qrLinks.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
          <QrCode className="h-5 w-5 mr-2" />
          QR-Code Vorschau
        </h3>
        <p className="text-gray-500 text-center py-8">
          Noch keine QR-Codes erstellt. 
          <button 
            onClick={() => navigate('/qr/dash/links')}
            className="text-blue-600 hover:underline ml-1"
          >
            Ersten QR-Code erstellen
          </button>
        </p>
      </div>
    )
  }

  const visibleQRs = qrLinks.slice(currentIndex, currentIndex + qrsPerView)
  const canGoPrevious = currentIndex > 0
  const canGoNext = currentIndex + qrsPerView < qrLinks.length

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <QrCode className="h-5 w-5 mr-2" />
          QR-Code Vorschau
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevious}
            disabled={!canGoPrevious}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-500">
            {currentIndex + 1}-{Math.min(currentIndex + qrsPerView, qrLinks.length)} von {qrLinks.length}
          </span>
          <button
            onClick={handleNext}
            disabled={!canGoNext}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className={`grid grid-cols-1 ${qrsPerView >= 2 ? 'sm:grid-cols-2' : ''} ${qrsPerView >= 3 ? 'lg:grid-cols-3' : ''} gap-4`}>
        {visibleQRs.map((qrLink) => (
          <div
            key={qrLink.id}
            onClick={() => handleQRClick(qrLink)}
            className="cursor-pointer group"
          >
            <div className="relative overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow bg-gray-50 border border-gray-200">
              {/* Preview im Mobile Format - kompakter */}
              <div className="aspect-[3/4] overflow-hidden relative bg-white">
                <div className="transform scale-[0.4] origin-top-left w-[250%] h-[250%]">
                  {getPreviewComponent(qrLink)}
                </div>
                
                {/* Mobile indicator */}
                <div className="absolute top-2 right-2 bg-white/90 p-1 rounded-full shadow-sm">
                  <Smartphone className="w-4 h-4 text-gray-600" />
                </div>
                
                {/* Health status indicator */}
                {qrLink.health_status === 'error' && (
                  <div className="absolute top-2 left-2 bg-red-500 text-white p-1 rounded-full" title={qrLink.health_error}>
                    <XCircle className="w-4 h-4" />
                  </div>
                )}
                {qrLink.health_status === 'warning' && (
                  <div className="absolute top-2 left-2 bg-yellow-500 text-white p-1 rounded-full" title={qrLink.health_error}>
                    <AlertTriangle className="w-4 h-4" />
                  </div>
                )}
              </div>
              
              {/* QR Code and Info */}
              <div className="p-4 bg-white border-t border-gray-200">
                <div className="flex items-start gap-3">
                  {/* QR Code */}
                  <img
                    src={getQRCodeUrl(qrLink)}
                    alt={`QR Code für ${qrLink.title}`}
                    className="w-16 h-16 rounded shadow-sm"
                  />
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                      {qrLink.title}
                    </h4>
                    <p className="text-xs text-gray-500 truncate">
                      {qrLink.type === 'multishop' ? 'Multi-Shop' : 
                       qrLink.type === 'author' ? 'Autorenprofil' :
                       qrLink.type === 'sample' ? 'Leseprobe' :
                       qrLink.type.charAt(0).toUpperCase() + qrLink.type.slice(1)}
                    </p>
                    {qrLink.scans !== undefined && (
                      <p className="text-xs text-gray-600 mt-1">
                        {qrLink.scans} Scan{qrLink.scans !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  
                  {/* External link */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      const url = qrLink.author_account_slug 
                        ? `${window.location.origin}/qr/${qrLink.author_account_slug}/${qrLink.slug}`
                        : `${window.location.origin}/r/${qrLink.slug}`
                      window.open(url, '_blank')
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Im neuen Tab öffnen"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
